/**
 * Treatment Management Context
 * Thin API client consuming the canonical backend treatment catalog.
 * Source of truth: /api/public/treatments (read), /api/admin/treatments (write)
 *
 * P0-A.3D — Provider Onboarding Treatment Source Canonicalization (flag-gated)
 * =============================================================================
 * When ``REACT_APP_PROVIDER_ONBOARDING_TREATMENT_SOURCE_CANONICAL`` is truthy
 * (``"true"`` / ``"1"`` / ``"yes"`` / ``"on"``), ``fetchTreatments()`` consumes
 * the role-free canonical onboarding catalog at
 * ``/api/public/catalog/treatments`` (41 rows; same data the canonical
 * onboarding "master catalog" already uses) and runs the additive
 * ``_adaptCatalogTreatmentToContext`` adapter to fill the
 * ``isActive`` field (deterministic ``true`` since the catalog endpoint
 * already filters out inactive rows). When the flag is OFF (default), the
 * legacy dual-path (auth → ``/admin/treatments`` → fallback
 * ``/public/treatments``) is preserved byte-identically.
 *
 * Rationale: this dual-path is the last onboarding-adjacent legacy drift
 * surface — new providers post-onboarding hit the auth branch and see a
 * 41-row admin list, while anonymous users see a 10-row published-only
 * list. Routing both through ``/public/catalog/treatments`` collapses the
 * drift into one canonical, role-free source.
 *
 * Default: OFF (byte-identical pre-state). Owner-ratified flip ON via
 * ``frontend/.env`` + rebuild/restart.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';

const TreatmentManagementContext = createContext();

export const useTreatmentManagement = () => {
  const context = useContext(TreatmentManagementContext);
  if (!context) {
    throw new Error('useTreatmentManagement must be used within TreatmentManagementProvider');
  }
  return context;
};

// Hardcoded fallback — the real source of truth is loaded from /api/public/treatment-taxonomy
export const TREATMENT_CATEGORIES = {
  DENTAL: { id: 'dental', label: 'Dental', icon: '🦷' },
  COSMETIC: { id: 'cosmetic', label: 'Cosmetic Surgery', icon: '💉' },
  HAIR: { id: 'hair', label: 'Hair Restoration', icon: '💇' },
  ORTHOPEDIC: { id: 'orthopedic', label: 'Orthopedic', icon: '🦴' },
  CARDIAC: { id: 'cardiac', label: 'Cardiac', icon: '❤️' },
  FERTILITY: { id: 'fertility', label: 'Fertility', icon: '👶' },
  OPHTHALMOLOGY: { id: 'ophthalmology', label: 'Eye Care', icon: '👁️' },
  BARIATRIC: { id: 'bariatric', label: 'Bariatric', icon: '⚖️' }
};

function getAuthHeader() {
  const token = '';
  return {};
}

// ── P0-A.3D — Flag helper + adapter ─────────────────────────────────
//
// Truthy parsing mirrors the backend convention used in P0-A.2 /
// P0-A.3C.2 / P0-A.3C.3 flags ({"1","true","yes","on"}, case-insensitive,
// whitespace-tolerant).
export function _onboardingCanonicalSourceEnabled() {
  const raw = (process.env.REACT_APP_PROVIDER_ONBOARDING_TREATMENT_SOURCE_CANONICAL || '');
  return ['1', 'true', 'yes', 'on'].includes(String(raw).trim().toLowerCase());
}

// Map ``/api/public/catalog/treatments`` row → context-expected shape.
//
// The canonical catalog endpoint (``/api/public/catalog/treatments``,
// served by ``image_pipeline_routes.py``) filters out inactive rows on
// the server side, so the adapter deterministically stamps
// ``isActive: true`` for every adapted row.
//
// Other downstream-required fields preserve the source value
// (``priceRange``, ``image``, ``shortDescription``, etc.). Missing
// fields default to ``undefined`` — the same shape any
// ``/public/treatments`` row would carry when its own fields are
// unset.
export function _adaptCatalogTreatmentToContext(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.slug,
    // Deterministic from inclusion semantics:
    isActive: true,
    // ``status`` is not present on catalog rows; canonical published rows
    // carry "published". This is informational only — no consumer
    // dereferences `status` from the context today, but we set it for
    // shape parity with /public/treatments responses.
    status: row.status || 'published',
  };
}

export const TreatmentManagementProvider = ({ children }) => {
  const [treatments, setTreatments] = useState([]);
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // TREATX-SEO-RECOVERY-S3A.1 — `loaded` alone cannot tell a SUCCESSFUL EMPTY
  // catalogue apart from a FAILED request: `setLoaded(true)` runs in `finally`,
  // so both end up as "loaded, zero rows". Consumers that must choose between
  // a permanent 404 and a temporary 503 need that distinction, so the fetch
  // records whether it actually succeeded. Additive: every existing consumer
  // of `loaded` and `treatments` is unaffected.
  const [catalogError, setCatalogError] = useState(false);

  // Fetch all treatments — canonical source is `/api/public/catalog/treatments`
  // (65 rows, role-free, matches admin CRUD). Owner UAT Issue 9: earlier
  // dual-path (auth probe → /admin/treatments → /public/treatments) fell
  // through to /public/treatments (11 rows) because the "hasToken" check
  // was `null`. Collapsed to the canonical catalog to eliminate drift.
  const fetchTreatments = useCallback(async () => {
    try {
      const res = await api.get('/public/catalog/treatments');
      const data = res.data;
      const mapped = (data.treatments || []).map(_adaptCatalogTreatmentToContext);
      setTreatments(mapped);
      // A 200 is a 200 — an empty catalogue is a valid, successful answer.
      setCatalogError(false);
    } catch (e) {
      console.error('Failed to load treatments:', e);
      // Best-effort fallback to /admin/treatments (also 65) so admins
      // never see a blank screen if /public/catalog is unreachable.
      try {
        const res = await api.get('/admin/treatments');
        const mapped = (res.data.treatments || []).map(t => ({ ...t, id: t.slug }));
        setTreatments(mapped);
        setCatalogError(false);
      } catch (_) {
        // Both the canonical endpoint and the fallback failed: the catalogue
        // is genuinely unavailable. Whatever `treatments` holds is stale or
        // empty, and no consumer may read "absent" as "does not exist".
        setCatalogError(true);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  // Fetch taxonomy categories from backend — single source of truth
  const fetchTaxonomyCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/public/treatment-taxonomy');
      setTaxonomyCategories(
        (data.categories || []).filter(c => c.status === 'active')
      );
    } catch (e) {
      console.error('Failed to load taxonomy categories:', e);
    }
  }, []);

  useEffect(() => {
    fetchTreatments();
    fetchTaxonomyCategories();
  }, [fetchTreatments, fetchTaxonomyCategories]);

  // ── Getters (identical signatures to previous Context) ──

  const getAllTreatments = useCallback(() => treatments, [treatments]);

  const getActiveTreatments = useCallback(() =>
    treatments.filter(t => t.isActive), [treatments]);

  const getTreatmentById = useCallback((id) =>
    treatments.find(t => t.id === id || t.slug === id), [treatments]);

  const getTreatmentBySlug = useCallback((slug) =>
    treatments.find(t => t.slug === slug), [treatments]);

  const getTreatmentsByCategory = useCallback((category) =>
    treatments.filter(t => t.category === category && t.isActive), [treatments]);

  const getPriceRange = useCallback((treatment) => {
    if (treatment.priceMode === 'manual' && treatment.manualPriceMin && treatment.manualPriceMax) {
      return { min: treatment.manualPriceMin, max: treatment.manualPriceMax };
    }
    return { min: treatment.priceRange?.min || 0, max: treatment.priceRange?.max || 0 };
  }, []);

  const getClinicsOfferingCount = useCallback(() => 0, []);

  // ── Mutations (call backend then refresh) ──

  const addTreatment = useCallback(async (treatmentData) => {
    try {
      const { data } = await api.post('/admin/treatments', treatmentData);
      await fetchTreatments();
      return data.treatment;
    } catch (e) {
      console.error('Failed to create treatment:', e);
    }
    return null;
  }, [fetchTreatments]);

  const updateTreatment = useCallback(async (id, updates) => {
    const slug = id;
    try {
      await api.put(`/admin/treatments/${slug}`, updates);
      await fetchTreatments();
    } catch (e) {
      console.error('Failed to update treatment:', e);
    }
  }, [fetchTreatments]);

  const deleteTreatment = useCallback(async (id) => {
    const slug = id;
    try {
      await api.delete(`/admin/treatments/${slug}`);
      await fetchTreatments();
    } catch (e) {
      console.error('Failed to delete treatment:', e);
    }
  }, [fetchTreatments]);

  const togglePriceMode = useCallback(async (id, mode) => {
    await updateTreatment(id, { priceMode: mode });
  }, [updateTreatment]);

  const updateManualPrices = useCallback(async (id, min, max) => {
    await updateTreatment(id, { manualPriceMin: min, manualPriceMax: max, priceMode: 'manual' });
  }, [updateTreatment]);

  const value = {
    treatments,
    loaded,
    catalogError,
    getAllTreatments,
    getActiveTreatments,
    getTreatmentById,
    getTreatmentBySlug,
    getTreatmentsByCategory,
    getPriceRange,
    getClinicsOfferingCount,
    addTreatment,
    updateTreatment,
    deleteTreatment,
    togglePriceMode,
    updateManualPrices,
    TREATMENT_CATEGORIES,
    taxonomyCategories,
    fetchTreatments,
  };

  return (
    <TreatmentManagementContext.Provider value={value}>
      {children}
    </TreatmentManagementContext.Provider>
  );
};

export default TreatmentManagementContext;
